import {
  GetBuyingPowerResponse,
  NetExposure,
  PnlData,
  SnapshotData,
} from '../../../../apis/statistics/statistics-api.def.ts'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import { useIsMobile } from '../../../../common/hooks/use-is-mobile.ts'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import { convertPxToNumber } from '../../../../utils/helpers.ts'
import { selectIdeaListInput } from '../../../../redux/slices/idea-list-input.slice.ts'
import { selectUser } from '../../../../features/user/user.slice.ts'
import { IdeaSummaryBpUsageCard } from './idea-summary-bp-usage.tsx'
import { IdeaSummaryNetExposure } from './idea-summary-net-exposure.tsx'
import { IdeaSummaryDailyPnlCard } from './idea-summary-daily-pnl.tsx'
import { IdeaSummaryStarScore } from './idea-summary-star-score.tsx'
import { useEffect, useRef } from 'react'
import { useTutorial } from '../../../../contexts/use-tutorial.ts'

interface SummaryInfoProps {
  useSnapshotData?: boolean
  buyingPower?: GetBuyingPowerResponse
  netExposure?: NetExposure
  pnlData?: PnlData
  snapshotData?: SnapshotData
  isBuyingPowerLoading?: boolean
  isNetExposureLoading?: boolean
  isFullyPnlLoading?: boolean
  deployment?: number
  starScore?: string
  previousScore?: string
  isStarScoreLoading?: boolean
}

export const SummaryInfo: React.FC<SummaryInfoProps> = ({
  useSnapshotData,
  buyingPower,
  netExposure,
  pnlData,
  snapshotData,
  isBuyingPowerLoading,
  isNetExposureLoading,
  isFullyPnlLoading,
  deployment,
  starScore,
  previousScore,
  isStarScoreLoading,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const isMobile = useIsMobile(430)
  const ideaListInput = useAppSelector(selectIdeaListInput)
  const userInfo = useAppSelector(selectUser)
  const summaryDataRef = useRef(null)
  const { setRef } = useTutorial()

  useEffect(() => {
    setRef('summaryData', summaryDataRef)

    return () => setRef('summaryData', null) // Cleanup when unmounting
  }, [setRef])

  const shouldShowSelectContributorMsg =
    ideaListInput.contributor === '' && !userInfo.bookId

  const usedBuyingPower = useSnapshotData
    ? (snapshotData?.usedBuyingPower ?? buyingPower?.data.usedBuyingPower ?? 0)
    : (buyingPower?.data.usedBuyingPower ?? snapshotData?.usedBuyingPower ?? 0)

  const totalBuyingPower = useSnapshotData
    ? (snapshotData?.buyingPower ?? buyingPower?.data.buyingPower ?? 0)
    : (buyingPower?.data.buyingPower ?? snapshotData?.buyingPower ?? 0)

  const netExposureOnGMV = useSnapshotData
    ? (snapshotData?.netExposureOnGMV ?? netExposure?.netExposureOnGMV ?? 0)
    : (netExposure?.netExposureOnGMV ?? snapshotData?.netExposureOnGMV ?? 0)

  const rawNetCategory = netExposure?.netCategory ?? userInfo.region
  const netCategory =
    rawNetCategory === 'Low Net'
      ? 'Greater China - Low Net'
      : rawNetCategory === 'Long Bias'
        ? 'Greater China - Long Bias'
        : rawNetCategory

  const dailyPnl = useSnapshotData
    ? (snapshotData?.dailyPnl ?? pnlData?.dailyPnl ?? 0)
    : (pnlData?.dailyPnl ?? snapshotData?.dailyPnl ?? 0)

  const wtdPnl = useSnapshotData
    ? (snapshotData?.wtdPnl ?? pnlData?.wtdPnl ?? 0)
    : (pnlData?.wtdPnl ?? snapshotData?.wtdPnl ?? 0)

  const mtdPnl = useSnapshotData
    ? (snapshotData?.mtdPnl ?? pnlData?.mtdPnl ?? 0)
    : (pnlData?.mtdPnl ?? snapshotData?.mtdPnl ?? 0)

  const qtdPnl = useSnapshotData
    ? (snapshotData?.qtdPnl ?? pnlData?.qtdPnl ?? 0)
    : (pnlData?.qtdPnl ?? snapshotData?.qtdPnl ?? 0)

  const ytdPnl = useSnapshotData
    ? (snapshotData?.ytdPnl ?? pnlData?.ytdPnl ?? 0)
    : (pnlData?.ytdPnl ?? snapshotData?.ytdPnl ?? 0)

  //const gmv = useSnapshotData
  //  ? (snapshotData?.gmv ?? netExposure?.gmv ?? 0)
  //  : (netExposure?.gmv ?? snapshotData?.gmv ?? 0)

  const lmv = useSnapshotData
    ? (snapshotData?.lmv ?? netExposure?.lmv ?? 0)
    : (netExposure?.lmv ?? snapshotData?.lmv ?? 0)

  const smv = useSnapshotData
    ? (snapshotData?.smv ?? netExposure?.smv ?? 0)
    : (netExposure?.smv ?? snapshotData?.smv ?? 0)

  const deploymentValue = deployment ?? 0

  const starScoreValue = useSnapshotData
    ? (snapshotData?.latestScore ?? starScore ?? '')
    : (starScore ?? snapshotData?.latestScore ?? '')

  const previousScoreValue = useSnapshotData
    ? (snapshotData?.previousScore ?? previousScore ?? '')
    : (previousScore ?? snapshotData?.previousScore ?? '')

  return (
    <Row
      ref={summaryDataRef}
      gutter={[
        isMobile ? 0 : convertPxToNumber(themeStyle.marginBase),
        isMobile ? 0 : convertPxToNumber(themeStyle.marginBase),
      ]}
      wrap={true}
    >
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={6}
        xxl={6}
        style={{ marginBottom: isMobile ? 16 : 0 }}
      >
        <IdeaSummaryBpUsageCard
          shouldShowSelectContributorMsg={shouldShowSelectContributorMsg}
          isLoading={isBuyingPowerLoading}
          usedBuyingPower={usedBuyingPower}
          totalBuyingPower={totalBuyingPower}
          deployment={deploymentValue}
        />
      </Col>
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={6}
        xxl={6}
        style={{ marginBottom: isMobile ? 16 : 0 }}
      >
        <IdeaSummaryNetExposure
          shouldShowSelectContributorMsg={shouldShowSelectContributorMsg}
          isLoading={isFullyPnlLoading}
          netExposureOnGmv={netExposureOnGMV}
          groupName={netCategory}
          lmv={lmv}
          smv={smv}
        />
      </Col>
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={6}
        xxl={6}
        style={{ marginBottom: isMobile ? 16 : 0 }}
      >
        <IdeaSummaryDailyPnlCard
          shouldShowSelectContributorMsg={shouldShowSelectContributorMsg}
          isLoading={isNetExposureLoading}
          dailyPnl={dailyPnl}
          totalBp={totalBuyingPower}
          wtd={wtdPnl}
          mtd={mtdPnl}
          qtd={qtdPnl}
          ytd={ytdPnl}
        />
      </Col>
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={6}
        xxl={6}
        style={{ marginBottom: isMobile ? 16 : 0 }}
      >
        <IdeaSummaryStarScore
          shouldShowSelectContributorMsg={shouldShowSelectContributorMsg}
          isLoading={isStarScoreLoading}
          score={starScoreValue}
          previousScore={previousScoreValue}
        />
      </Col>
    </Row>
  )
}
